import { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { 
  Calendar, DollarSign, Users, TrendingUp, 
  Clock, CheckCircle, Star, Settings, User,
  Plus, Image, MapPin, Upload, X, Trash2, MessageSquare, Mail, Phone,
  Wallet, PiggyBank, ArrowUpRight, ArrowDownRight, Percent, CreditCard, ExternalLink,
  AlertTriangle, Link2, Banknote, CalendarDays, CalendarX, CalendarCheck
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { Calendar as CalendarComponent } from "../components/ui/calendar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import DashboardLayout from "../components/DashboardLayout";
import { API, useAuth } from "../App";
import { toast } from "sonner";

const categories = [
  { id: "dj", name: "DJ" },
  { id: "musician", name: "Musician" },
  { id: "singer", name: "Singer" },
  { id: "mc", name: "MC" },
  { id: "comedian", name: "Comedian" },
  { id: "photographer", name: "Photographer" },
  { id: "caterer", name: "Caterer" },
  { id: "baker", name: "Baker" },
  { id: "decorator", name: "Decorator" },
  { id: "balloon_artist", name: "Balloon Artist" },
  { id: "security", name: "Security" },
];

const VendorDashboard = () => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [vendorProfile, setVendorProfile] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateProfile, setShowCreateProfile] = useState(false);
  const [stripeStatus, setStripeStatus] = useState(null);
  const [stripeLoading, setStripeLoading] = useState(false);
  
  // Availability state
  const [availability, setAvailability] = useState({ blocked_dates: [], booked_dates: [] });
  const [selectedDates, setSelectedDates] = useState([]);
  const [savingAvailability, setSavingAvailability] = useState(false);
  
  const [profileForm, setProfileForm] = useState({
    business_name: "",
    category: "",
    description: "",
    location: "",
    hourly_rate: "",
    services: ""
  });

  const activeTab = searchParams.get("tab") || "overview";

  useEffect(() => {
    fetchVendorProfile();
    fetchBookings();
    fetchInquiries();
    
    // Check for Stripe redirect
    const stripeConnected = searchParams.get("stripe_connected");
    const stripeRefresh = searchParams.get("stripe_refresh");
    
    if (stripeConnected === "true") {
      toast.success("Stripe account connected successfully!");
      fetchStripeStatus();
      // Clean up URL
      searchParams.delete("stripe_connected");
      setSearchParams(searchParams);
    }
    
    if (stripeRefresh === "true") {
      toast.info("Please complete your Stripe setup");
      // Clean up URL
      searchParams.delete("stripe_refresh");
      setSearchParams(searchParams);
    }
  }, []);

  const fetchVendorProfile = async () => {
    try {
      const response = await axios.get(`${API}/vendors/my/profile`, { withCredentials: true });
      setVendorProfile(response.data);
      // Fetch Stripe status after getting profile
      fetchStripeStatus();
      // Fetch availability
      fetchAvailability(response.data.vendor_id);
    } catch (error) {
      if (error.response?.status === 404) {
        setShowCreateProfile(true);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailability = async (vendorId) => {
    try {
      const response = await axios.get(`${API}/vendors/${vendorId}/availability`, { withCredentials: true });
      setAvailability(response.data);
    } catch (error) {
      console.error("Error fetching availability:", error);
    }
  };

  const handleBlockDates = async () => {
    if (selectedDates.length === 0) {
      toast.error("Please select dates to block");
      return;
    }
    
    setSavingAvailability(true);
    try {
      const datesToBlock = selectedDates.map(d => d.toISOString().split('T')[0]);
      await axios.post(`${API}/vendors/my/availability/block`, { dates: datesToBlock }, { withCredentials: true });
      toast.success(`${selectedDates.length} date(s) blocked`);
      setSelectedDates([]);
      fetchAvailability(vendorProfile.vendor_id);
    } catch (error) {
      console.error("Error blocking dates:", error);
      toast.error("Failed to block dates");
    } finally {
      setSavingAvailability(false);
    }
  };

  const handleUnblockDates = async () => {
    if (selectedDates.length === 0) {
      toast.error("Please select dates to unblock");
      return;
    }
    
    setSavingAvailability(true);
    try {
      const datesToUnblock = selectedDates.map(d => d.toISOString().split('T')[0]);
      await axios.post(`${API}/vendors/my/availability/unblock`, { dates: datesToUnblock }, { withCredentials: true });
      toast.success(`${selectedDates.length} date(s) unblocked`);
      setSelectedDates([]);
      fetchAvailability(vendorProfile.vendor_id);
    } catch (error) {
      console.error("Error unblocking dates:", error);
      toast.error("Failed to unblock dates");
    } finally {
      setSavingAvailability(false);
    }
  };

  const fetchStripeStatus = async () => {
    try {
      const response = await axios.get(`${API}/vendors/stripe/status`, { withCredentials: true });
      setStripeStatus(response.data);
    } catch (error) {
      console.error("Error fetching Stripe status:", error);
    }
  };

  const handleConnectStripe = async () => {
    setStripeLoading(true);
    try {
      const response = await axios.post(`${API}/vendors/stripe/connect`, {}, { withCredentials: true });
      // Redirect to Stripe onboarding
      window.location.href = response.data.url;
    } catch (error) {
      console.error("Error connecting Stripe:", error);
      toast.error(error.response?.data?.detail || "Failed to connect Stripe");
      setStripeLoading(false);
    }
  };

  const handleOpenStripeDashboard = async () => {
    try {
      const response = await axios.post(`${API}/vendors/stripe/dashboard-link`, {}, { withCredentials: true });
      window.open(response.data.url, "_blank");
    } catch (error) {
      console.error("Error opening Stripe dashboard:", error);
      toast.error("Failed to open Stripe dashboard");
    }
  };

  const fetchBookings = async () => {
    try {
      const response = await axios.get(`${API}/bookings`, { withCredentials: true });
      setBookings(response.data);
    } catch (error) {
      console.error("Error fetching bookings:", error);
    }
  };

  const fetchInquiries = async () => {
    try {
      const response = await axios.get(`${API}/inquiries`, { withCredentials: true });
      setInquiries(response.data);
    } catch (error) {
      console.error("Error fetching inquiries:", error);
    }
  };

  const handleCreateProfile = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/vendors`, {
        ...profileForm,
        hourly_rate: parseFloat(profileForm.hourly_rate),
        services: profileForm.services.split(",").map(s => s.trim()).filter(Boolean)
      }, { withCredentials: true });
      
      setVendorProfile(response.data);
      setShowCreateProfile(false);
      toast.success("Vendor profile created!");
    } catch (error) {
      console.error("Error creating profile:", error);
      toast.error("Failed to create profile");
    }
  };

  const handleUpdateBookingStatus = async (bookingId, status) => {
    try {
      await axios.put(`${API}/bookings/${bookingId}/status?status=${status}`, {}, { withCredentials: true });
      toast.success("Booking status updated");
      fetchBookings();
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("Failed to update status");
    }
  };

  const handleDeletePortfolioImage = async (imageUrl) => {
    // Extract file_id from URL
    const filename = imageUrl.split('/').pop();
    const fileId = filename.split('.')[0];
    
    try {
      await axios.delete(`${API}/upload/portfolio/${fileId}`, { withCredentials: true });
      toast.success("Image deleted");
      fetchVendorProfile();
    } catch (error) {
      console.error("Error deleting image:", error);
      toast.error("Failed to delete image");
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: "bg-yellow-500/10 text-yellow-500 border-yellow-500/20",
      confirmed: "bg-blue-500/10 text-blue-500 border-blue-500/20",
      completed: "bg-green-500/10 text-green-500 border-green-500/20",
      cancelled: "bg-red-500/10 text-red-500 border-red-500/20"
    };
    return styles[status] || styles.pending;
  };

  // Stats - Enhanced with earnings breakdown (10% fee with $25 minimum)
  const completedBookings = bookings.filter(b => b.status === "completed");
  const paidBookings = bookings.filter(b => b.payment_status === "fully_paid" || b.payment_status === "deposit_paid");
  const grossEarnings = paidBookings.reduce((sum, b) => sum + b.total_amount, 0);
  
  // Calculate platform fee: 10% with $25 minimum per booking
  const calculateFee = (amount) => Math.max(amount * 0.10, 25);
  const platformFee = paidBookings.reduce((sum, b) => sum + calculateFee(b.total_amount), 0);
  const netEarnings = grossEarnings - platformFee;
  const pendingPayouts = bookings.filter(b => b.payment_status === "deposit_paid").reduce((sum, b) => sum + (b.total_amount - b.deposit_amount), 0);
  
  const stats = {
    totalBookings: bookings.length,
    pendingBookings: bookings.filter(b => b.status === "pending").length,
    completedBookings: completedBookings.length,
    grossEarnings,
    platformFee,
    netEarnings,
    pendingPayouts
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-full">
          <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-10">
        {/* Create Profile Dialog */}
        <Dialog open={showCreateProfile} onOpenChange={setShowCreateProfile}>
          <DialogContent className="bg-charcoal border-white/10 text-white max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl">Create Vendor Profile</DialogTitle>
            </DialogHeader>
            
            <form onSubmit={handleCreateProfile} className="space-y-4 mt-4">
              <div>
                <Label htmlFor="business_name" className="text-white/60">Business Name</Label>
                <Input
                  id="business_name"
                  value={profileForm.business_name}
                  onChange={(e) => setProfileForm({ ...profileForm, business_name: e.target.value })}
                  required
                  className="bg-obsidian border-white/10 text-white mt-1"
                  data-testid="vendor-business-name"
                />
              </div>
              
              <div>
                <Label className="text-white/60">Category</Label>
                <Select 
                  value={profileForm.category} 
                  onValueChange={(v) => setProfileForm({ ...profileForm, category: v })}
                >
                  <SelectTrigger className="bg-obsidian border-white/10 text-white mt-1" data-testid="vendor-category">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent className="bg-charcoal border-white/10">
                    {categories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id} className="text-white hover:bg-white/5">
                        {cat.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="description" className="text-white/60">Description</Label>
                <Textarea
                  id="description"
                  value={profileForm.description}
                  onChange={(e) => setProfileForm({ ...profileForm, description: e.target.value })}
                  required
                  rows={3}
                  className="bg-obsidian border-white/10 text-white mt-1"
                  data-testid="vendor-description"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="location" className="text-white/60">Location</Label>
                  <Input
                    id="location"
                    value={profileForm.location}
                    onChange={(e) => setProfileForm({ ...profileForm, location: e.target.value })}
                    required
                    placeholder="City, State"
                    className="bg-obsidian border-white/10 text-white mt-1"
                    data-testid="vendor-location"
                  />
                </div>
                <div>
                  <Label htmlFor="hourly_rate" className="text-white/60">Hourly Rate ($)</Label>
                  <Input
                    id="hourly_rate"
                    type="number"
                    value={profileForm.hourly_rate}
                    onChange={(e) => setProfileForm({ ...profileForm, hourly_rate: e.target.value })}
                    required
                    min="1"
                    className="bg-obsidian border-white/10 text-white mt-1"
                    data-testid="vendor-rate"
                  />
                </div>
              </div>
              
              <div>
                <Label htmlFor="services" className="text-white/60">Services (comma-separated)</Label>
                <Input
                  id="services"
                  value={profileForm.services}
                  onChange={(e) => setProfileForm({ ...profileForm, services: e.target.value })}
                  placeholder="Wedding DJ, Corporate Events, Parties"
                  className="bg-obsidian border-white/10 text-white mt-1"
                  data-testid="vendor-services"
                />
              </div>
              
              <Button 
                type="submit" 
                className="w-full bg-gold text-black hover:bg-gold-light rounded-full font-bold"
                data-testid="create-profile-btn"
              >
                Create Profile
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        <div className="mb-8">
          <h1 className="font-heading text-3xl font-bold text-white mb-2">
            Vendor Dashboard
          </h1>
          <p className="text-white/60">
            {vendorProfile ? vendorProfile.business_name : "Set up your vendor profile"}
          </p>
        </div>

        {vendorProfile && (
          <>
            {/* Stats Cards - Enhanced with earnings breakdown */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
              {[
                { label: "Total Bookings", value: stats.totalBookings, icon: Calendar, color: "text-blue-400" },
                { label: "Pending", value: stats.pendingBookings, icon: Clock, color: "text-yellow-400" },
                { label: "Completed", value: stats.completedBookings, icon: CheckCircle, color: "text-green-400" },
                { label: "Net Earnings", value: `$${stats.netEarnings.toLocaleString()}`, icon: Wallet, color: "text-gold" },
              ].map((stat, i) => (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1 }}
                  className="bg-charcoal rounded-xl border border-white/5 p-6"
                >
                  <div className="flex items-center justify-between mb-2">
                    <stat.icon className={`w-5 h-5 ${stat.color}`} />
                  </div>
                  <p className="font-heading text-2xl font-bold text-white">{stat.value}</p>
                  <p className="text-white/40 text-sm">{stat.label}</p>
                </motion.div>
              ))}
            </div>

            {/* Financial Summary Row */}
            <div className="grid grid-cols-3 gap-4 mb-8">
              <div className="bg-charcoal rounded-xl border border-white/5 p-4">
                <div className="flex items-center gap-2 mb-1">
                  <ArrowUpRight className="w-4 h-4 text-green-400" />
                  <span className="text-white/60 text-sm">Gross Earnings</span>
                </div>
                <p className="font-heading text-xl font-bold text-green-400">${stats.grossEarnings.toLocaleString()}</p>
              </div>
              <div className="bg-charcoal rounded-xl border border-white/5 p-4">
                <div className="flex items-center gap-2 mb-1">
                  <Percent className="w-4 h-4 text-red-400" />
                  <span className="text-white/60 text-sm">Platform Fee (10%, $25 min)</span>
                </div>
                <p className="font-heading text-xl font-bold text-red-400">-${stats.platformFee.toLocaleString()}</p>
              </div>
              <div className="bg-charcoal rounded-xl border border-white/5 p-4">
                <div className="flex items-center gap-2 mb-1">
                  <PiggyBank className="w-4 h-4 text-yellow-400" />
                  <span className="text-white/60 text-sm">Pending Payouts</span>
                </div>
                <p className="font-heading text-xl font-bold text-yellow-400">${stats.pendingPayouts.toLocaleString()}</p>
              </div>
            </div>

            <Tabs value={activeTab} onValueChange={(v) => setSearchParams({ tab: v })}>
              <TabsList className="bg-charcoal border border-white/5 rounded-full p-1 mb-8">
                <TabsTrigger 
                  value="overview" 
                  className="rounded-full data-[state=active]:bg-gold data-[state=active]:text-black"
                  data-testid="vendor-tab-overview"
                >
                  Overview
                </TabsTrigger>
                <TabsTrigger 
                  value="bookings" 
                  className="rounded-full data-[state=active]:bg-gold data-[state=active]:text-black"
                  data-testid="vendor-tab-bookings"
                >
                  Bookings
                </TabsTrigger>
                <TabsTrigger 
                  value="inquiries" 
                  className="rounded-full data-[state=active]:bg-gold data-[state=active]:text-black"
                  data-testid="vendor-tab-inquiries"
                >
                  Inquiries
                  {inquiries.filter(i => i.status === 'pending').length > 0 && (
                    <span className="ml-2 bg-gold text-black text-xs rounded-full px-2 py-0.5">
                      {inquiries.filter(i => i.status === 'pending').length}
                    </span>
                  )}
                </TabsTrigger>
                <TabsTrigger 
                  value="earnings" 
                  className="rounded-full data-[state=active]:bg-gold data-[state=active]:text-black"
                  data-testid="vendor-tab-earnings"
                >
                  Earnings
                </TabsTrigger>
                <TabsTrigger 
                  value="availability" 
                  className="rounded-full data-[state=active]:bg-gold data-[state=active]:text-black"
                  data-testid="vendor-tab-availability"
                >
                  Availability
                </TabsTrigger>
                <TabsTrigger 
                  value="profile" 
                  className="rounded-full data-[state=active]:bg-gold data-[state=active]:text-black"
                  data-testid="vendor-tab-profile"
                >
                  Profile
                </TabsTrigger>
              </TabsList>

              <TabsContent value="overview">
                <div className="grid lg:grid-cols-2 gap-6">
                  {/* Recent Bookings */}
                  <div className="bg-charcoal rounded-xl border border-white/5 p-6">
                    <h3 className="font-heading text-lg font-bold text-white mb-4">Recent Bookings</h3>
                    {bookings.slice(0, 5).map((booking) => (
                      <div key={booking.booking_id} className="flex items-center justify-between py-3 border-b border-white/5 last:border-0">
                        <div>
                          <p className="text-white font-medium">{booking.customer?.name}</p>
                          <p className="text-white/40 text-sm">{booking.event_date}</p>
                        </div>
                        <Badge className={getStatusBadge(booking.status)} variant="outline">
                          {booking.status}
                        </Badge>
                      </div>
                    ))}
                    {bookings.length === 0 && (
                      <p className="text-white/40 text-center py-4">No bookings yet</p>
                    )}
                  </div>

                  {/* Profile Summary */}
                  <div className="bg-charcoal rounded-xl border border-white/5 p-6">
                    <h3 className="font-heading text-lg font-bold text-white mb-4">Profile Summary</h3>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <span className="text-white/60">Rating</span>
                        <div className="flex items-center">
                          <Star className="w-4 h-4 text-gold fill-gold mr-1" />
                          <span className="text-white">{vendorProfile.rating?.toFixed(1) || "New"}</span>
                          <span className="text-white/40 ml-1">({vendorProfile.review_count} reviews)</span>
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-white/60">Hourly Rate</span>
                        <span className="text-gold font-bold">${vendorProfile.hourly_rate}/hr</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-white/60">Location</span>
                        <span className="text-white">{vendorProfile.location}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-white/60">Status</span>
                        <Badge className={vendorProfile.is_verified ? "bg-green-500/10 text-green-500" : "bg-yellow-500/10 text-yellow-500"} variant="outline">
                          {vendorProfile.is_verified ? "Verified" : "Pending Verification"}
                        </Badge>
                      </div>
                    </div>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="bookings">
                <div className="space-y-4">
                  {bookings.map((booking) => (
                    <div 
                      key={booking.booking_id}
                      className="bg-charcoal rounded-xl border border-white/5 p-6"
                      data-testid={`vendor-booking-${booking.booking_id}`}
                    >
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div>
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="font-heading text-lg font-bold text-white">
                              {booking.customer?.name}
                            </h3>
                            <Badge className={getStatusBadge(booking.status)} variant="outline">
                              {booking.status}
                            </Badge>
                          </div>
                          <div className="flex flex-wrap items-center gap-4 text-white/60 text-sm">
                            <div className="flex items-center">
                              <Calendar className="w-4 h-4 mr-1" />
                              {booking.event_date} at {booking.event_time}
                            </div>
                            <div className="flex items-center">
                              <MapPin className="w-4 h-4 mr-1" />
                              {booking.event_location}
                            </div>
                            <div className="flex items-center">
                              <DollarSign className="w-4 h-4 mr-1" />
                              ${booking.total_amount}
                            </div>
                          </div>
                        </div>

                        <div className="flex gap-2">
                          {booking.status === "pending" && (
                            <>
                              <Button
                                size="sm"
                                onClick={() => handleUpdateBookingStatus(booking.booking_id, "confirmed")}
                                className="bg-green-500 hover:bg-green-600 text-white rounded-full"
                                data-testid={`confirm-booking-${booking.booking_id}`}
                              >
                                <CheckCircle className="w-4 h-4 mr-1" />
                                Confirm
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleUpdateBookingStatus(booking.booking_id, "cancelled")}
                                className="border-red-500/30 text-red-500 hover:bg-red-500/10 rounded-full"
                                data-testid={`cancel-booking-${booking.booking_id}`}
                              >
                                Cancel
                              </Button>
                            </>
                          )}
                          {booking.status === "confirmed" && (
                            <Button
                              size="sm"
                              onClick={() => handleUpdateBookingStatus(booking.booking_id, "completed")}
                              className="bg-gold text-black hover:bg-gold-light rounded-full"
                              data-testid={`complete-booking-${booking.booking_id}`}
                            >
                              Mark Complete
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                  {bookings.length === 0 && (
                    <div className="text-center py-12 bg-charcoal rounded-xl border border-white/5">
                      <Calendar className="w-12 h-12 text-gold/30 mx-auto mb-4" />
                      <p className="text-white/60">No bookings yet</p>
                    </div>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="inquiries">
                <div className="space-y-4">
                  {inquiries.map((inquiry) => (
                    <div 
                      key={inquiry.inquiry_id}
                      className="bg-charcoal rounded-xl border border-white/5 p-6"
                      data-testid={`inquiry-${inquiry.inquiry_id}`}
                    >
                      <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-3">
                            <h3 className="font-heading text-lg font-bold text-white">
                              {inquiry.name}
                            </h3>
                            <Badge 
                              className={inquiry.status === 'pending' 
                                ? "bg-yellow-500/10 text-yellow-500 border-yellow-500/20" 
                                : "bg-green-500/10 text-green-500 border-green-500/20"
                              } 
                              variant="outline"
                            >
                              {inquiry.status}
                            </Badge>
                          </div>
                          
                          <div className="grid sm:grid-cols-2 gap-3 text-sm mb-4">
                            <div className="flex items-center text-white/60">
                              <Mail className="w-4 h-4 mr-2 text-gold" />
                              {inquiry.email}
                            </div>
                            <div className="flex items-center text-white/60">
                              <Phone className="w-4 h-4 mr-2 text-gold" />
                              {inquiry.phone}
                            </div>
                            <div className="flex items-center text-white/60">
                              <Calendar className="w-4 h-4 mr-2 text-gold" />
                              Event: {inquiry.event_date}
                            </div>
                            <div className="flex items-center text-white/60">
                              <MessageSquare className="w-4 h-4 mr-2 text-gold" />
                              {inquiry.event_type}
                            </div>
                          </div>
                          
                          <div className="bg-obsidian rounded-lg p-4 border border-white/5">
                            <p className="text-white/60 text-sm mb-1">Message:</p>
                            <p className="text-white">{inquiry.message}</p>
                          </div>
                          
                          <p className="text-white/40 text-xs mt-3">
                            Received: {new Date(inquiry.created_at).toLocaleDateString()}
                          </p>
                        </div>

                        <div className="flex gap-2">
                          <a href={`mailto:${inquiry.email}`}>
                            <Button
                              size="sm"
                              className="bg-gold text-black hover:bg-gold-light rounded-full"
                            >
                              <Mail className="w-4 h-4 mr-1" />
                              Reply
                            </Button>
                          </a>
                          <a href={`tel:${inquiry.phone}`}>
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-white/20 text-white hover:bg-white/5 rounded-full"
                            >
                              <Phone className="w-4 h-4 mr-1" />
                              Call
                            </Button>
                          </a>
                        </div>
                      </div>
                    </div>
                  ))}
                  {inquiries.length === 0 && (
                    <div className="text-center py-12 bg-charcoal rounded-xl border border-white/5">
                      <MessageSquare className="w-12 h-12 text-gold/30 mx-auto mb-4" />
                      <p className="text-white/60">No inquiries yet</p>
                      <p className="text-white/40 text-sm mt-1">
                        Inquiries from potential customers will appear here
                      </p>
                    </div>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="earnings">
                <div className="space-y-6">
                  {/* Payment Model Explanation */}
                  <div className="rounded-xl border p-6 bg-green-500/10 border-green-500/20">
                    <div className="flex items-start gap-4">
                      <div className="p-3 rounded-xl bg-green-500/20">
                        <Banknote className="w-6 h-6 text-green-400" />
                      </div>
                      <div>
                        <h3 className="font-heading text-lg font-bold text-white mb-1">
                          Direct Payment Model
                        </h3>
                        <p className="text-white/60 text-sm mb-3">
                          You receive payment directly from clients at the time of your event. Rafha Entertainment only 
                          collects a 10% service fee ($25 minimum) from clients at booking - you keep the rest!
                        </p>
                        <div className="flex gap-4 mt-3">
                          <div className="bg-obsidian/50 rounded-lg px-4 py-2">
                            <p className="text-white/40 text-xs">Your Share</p>
                            <p className="text-green-400 font-bold">90%</p>
                          </div>
                          <div className="bg-obsidian/50 rounded-lg px-4 py-2">
                            <p className="text-white/40 text-xs">Platform Fee</p>
                            <p className="text-gold font-bold">10% (min $25)</p>
                          </div>
                          <div className="bg-obsidian/50 rounded-lg px-4 py-2">
                            <p className="text-white/40 text-xs">Payment Method</p>
                            <p className="text-white font-bold">Direct at Event</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Earnings Summary Card */}
                  <div className="bg-charcoal rounded-xl border border-white/5 p-6">
                    <h3 className="font-heading text-lg font-bold text-white mb-6 flex items-center gap-2">
                      <Wallet className="w-5 h-5 text-gold" />
                      Earnings Summary
                    </h3>
                    
                    <div className="grid md:grid-cols-3 gap-6 mb-6">
                      <div className="text-center p-4 bg-obsidian rounded-xl border border-white/5">
                        <p className="text-white/40 text-sm mb-1">Total Booking Value</p>
                        <p className="font-heading text-3xl font-bold text-green-400">${stats.grossEarnings.toLocaleString()}</p>
                        <p className="text-white/40 text-xs mt-1">From confirmed bookings</p>
                      </div>
                      <div className="text-center p-4 bg-obsidian rounded-xl border border-white/5">
                        <p className="text-white/40 text-sm mb-1">Platform Fee (Paid by Client)</p>
                        <p className="font-heading text-3xl font-bold text-white/40">${stats.platformFee.toLocaleString()}</p>
                        <p className="text-white/40 text-xs mt-1">Collected by Rafha</p>
                      </div>
                      <div className="text-center p-4 bg-gold/10 rounded-xl border border-gold/20">
                        <p className="text-gold/80 text-sm mb-1">Your Earnings</p>
                        <p className="font-heading text-3xl font-bold text-gold">${stats.netEarnings.toLocaleString()}</p>
                        <p className="text-gold/60 text-xs mt-1">Collected directly at events</p>
                      </div>
                    </div>

                    {/* How Payments Work */}
                    <div className="bg-obsidian/50 rounded-lg p-4 border border-white/5">
                      <div className="flex items-start gap-3">
                        <DollarSign className="w-5 h-5 text-gold mt-0.5" />
                        <div>
                          <p className="text-white font-medium mb-1">How You Get Paid</p>
                          <p className="text-white/60 text-sm">
                            When a client books you, they pay a non-refundable service fee to Rafha Entertainment. 
                            The remaining balance is paid <strong>directly to you</strong> at the time of your event. 
                            You can accept Cash, Venmo, Zelle, CashApp, or any payment method you prefer.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Booking Earnings Breakdown */}
                  <div className="bg-charcoal rounded-xl border border-white/5 p-6">
                    <h3 className="font-heading text-lg font-bold text-white mb-4 flex items-center gap-2">
                      <DollarSign className="w-5 h-5 text-gold" />
                      Booking Earnings Breakdown
                    </h3>
                    
                    {bookings.filter(b => b.payment_status !== "unpaid").length > 0 ? (
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead>
                            <tr className="border-b border-white/10">
                              <th className="text-left p-3 text-white/60 font-medium text-sm">Customer</th>
                              <th className="text-left p-3 text-white/60 font-medium text-sm">Event Date</th>
                              <th className="text-right p-3 text-white/60 font-medium text-sm">Gross</th>
                              <th className="text-right p-3 text-white/60 font-medium text-sm">Fee (10%, $25 min)</th>
                              <th className="text-right p-3 text-white/60 font-medium text-sm">Net</th>
                              <th className="text-center p-3 text-white/60 font-medium text-sm">Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {bookings
                              .filter(b => b.payment_status !== "unpaid")
                              .map((booking) => {
                                const gross = booking.total_amount;
                                const fee = Math.max(gross * 0.10, 25); // 10% with $25 minimum
                                const net = gross - fee;
                                return (
                                  <tr key={booking.booking_id} className="border-b border-white/5">
                                    <td className="p-3 text-white">{booking.customer?.name || "Customer"}</td>
                                    <td className="p-3 text-white/60">{booking.event_date}</td>
                                    <td className="p-3 text-right text-green-400">${gross.toLocaleString()}</td>
                                    <td className="p-3 text-right text-red-400">-${fee.toLocaleString()}</td>
                                    <td className="p-3 text-right text-gold font-medium">${net.toLocaleString()}</td>
                                    <td className="p-3 text-center">
                                      <Badge 
                                        className={
                                          booking.payment_status === "fully_paid"
                                            ? "bg-green-500/10 text-green-400 border-green-500/20"
                                            : "bg-yellow-500/10 text-yellow-400 border-yellow-500/20"
                                        }
                                        variant="outline"
                                      >
                                        {booking.payment_status === "fully_paid" ? "Paid" : "Deposit Only"}
                                      </Badge>
                                    </td>
                                  </tr>
                                );
                              })}
                          </tbody>
                          <tfoot className="border-t border-white/10">
                            <tr>
                              <td colSpan="2" className="p-3 text-white font-medium">Total</td>
                              <td className="p-3 text-right text-green-400 font-bold">${stats.grossEarnings.toLocaleString()}</td>
                              <td className="p-3 text-right text-red-400 font-bold">-${stats.platformFee.toLocaleString()}</td>
                              <td className="p-3 text-right text-gold font-bold">${stats.netEarnings.toLocaleString()}</td>
                              <td></td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    ) : (
                      <div className="text-center py-12 border-2 border-dashed border-white/10 rounded-xl">
                        <DollarSign className="w-12 h-12 text-gold/30 mx-auto mb-4" />
                        <p className="text-white/60 mb-2">No paid bookings yet</p>
                        <p className="text-white/40 text-sm">Your earnings will appear here once customers pay</p>
                      </div>
                    )}
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="availability">
                <div className="space-y-6">
                  {/* Calendar Section */}
                  <div className="bg-charcoal rounded-xl border border-white/5 p-6">
                    <div className="flex items-center justify-between mb-6">
                      <h3 className="font-heading text-lg font-bold text-white flex items-center gap-2">
                        <CalendarDays className="w-5 h-5 text-gold" />
                        Manage Your Availability
                      </h3>
                    </div>
                    
                    <div className="grid lg:grid-cols-2 gap-8">
                      {/* Calendar */}
                      <div>
                        <p className="text-white/60 text-sm mb-4">
                          Select dates to block or unblock. Red dates are blocked, blue dates have bookings.
                        </p>
                        <div className="bg-obsidian rounded-xl p-4 border border-white/5 flex justify-center">
                          <CalendarComponent
                            mode="multiple"
                            selected={selectedDates}
                            onSelect={setSelectedDates}
                            className="text-white"
                            modifiers={{
                              blocked: (date) => {
                                const dateStr = date.toISOString().split('T')[0];
                                return availability.blocked_dates?.includes(dateStr);
                              },
                              booked: (date) => {
                                const dateStr = date.toISOString().split('T')[0];
                                return availability.booked_dates?.includes(dateStr);
                              }
                            }}
                            modifiersStyles={{
                              blocked: { backgroundColor: 'rgba(239, 68, 68, 0.3)', color: '#ef4444' },
                              booked: { backgroundColor: 'rgba(59, 130, 246, 0.3)', color: '#3b82f6' }
                            }}
                            disabled={(date) => date < new Date()}
                          />
                        </div>
                        
                        <div className="flex gap-4 mt-4">
                          <Button
                            onClick={handleBlockDates}
                            disabled={selectedDates.length === 0 || savingAvailability}
                            className="flex-1 bg-red-500/20 text-red-400 hover:bg-red-500/30 rounded-full border border-red-500/30"
                          >
                            <CalendarX className="w-4 h-4 mr-2" />
                            Block Selected ({selectedDates.length})
                          </Button>
                          <Button
                            onClick={handleUnblockDates}
                            disabled={selectedDates.length === 0 || savingAvailability}
                            className="flex-1 bg-green-500/20 text-green-400 hover:bg-green-500/30 rounded-full border border-green-500/30"
                          >
                            <CalendarCheck className="w-4 h-4 mr-2" />
                            Unblock Selected
                          </Button>
                        </div>
                      </div>
                      
                      {/* Legend & Stats */}
                      <div className="space-y-6">
                        <div className="bg-obsidian rounded-xl p-4 border border-white/5">
                          <h4 className="text-white font-medium mb-4">Legend</h4>
                          <div className="space-y-3">
                            <div className="flex items-center gap-3">
                              <div className="w-4 h-4 rounded bg-red-500/30 border border-red-500/50"></div>
                              <span className="text-white/60 text-sm">Blocked (unavailable)</span>
                            </div>
                            <div className="flex items-center gap-3">
                              <div className="w-4 h-4 rounded bg-blue-500/30 border border-blue-500/50"></div>
                              <span className="text-white/60 text-sm">Booked (confirmed events)</span>
                            </div>
                            <div className="flex items-center gap-3">
                              <div className="w-4 h-4 rounded bg-gold/30 border border-gold/50"></div>
                              <span className="text-white/60 text-sm">Selected</span>
                            </div>
                          </div>
                        </div>
                        
                        <div className="bg-obsidian rounded-xl p-4 border border-white/5">
                          <h4 className="text-white font-medium mb-4">Quick Stats</h4>
                          <div className="grid grid-cols-2 gap-4">
                            <div className="text-center p-3 bg-charcoal rounded-lg">
                              <p className="text-2xl font-bold text-red-400">{availability.blocked_dates?.length || 0}</p>
                              <p className="text-white/40 text-xs">Blocked Dates</p>
                            </div>
                            <div className="text-center p-3 bg-charcoal rounded-lg">
                              <p className="text-2xl font-bold text-blue-400">{availability.booked_dates?.length || 0}</p>
                              <p className="text-white/40 text-xs">Booked Dates</p>
                            </div>
                          </div>
                        </div>
                        
                        <div className="bg-gold/10 rounded-xl p-4 border border-gold/20">
                          <h4 className="text-gold font-medium mb-2">Pro Tip</h4>
                          <p className="text-white/60 text-sm">
                            Keep your availability up to date to avoid double bookings. 
                            Blocked dates won't appear as available to customers during booking.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="profile">
                <div className="bg-charcoal rounded-xl border border-white/5 p-6">
                  <h3 className="font-heading text-lg font-bold text-white mb-6">Profile Settings</h3>
                  <div className="space-y-4">
                    <div>
                      <Label className="text-white/60">Business Name</Label>
                      <p className="text-white mt-1">{vendorProfile.business_name}</p>
                    </div>
                    <div>
                      <Label className="text-white/60">Category</Label>
                      <p className="text-white mt-1 capitalize">{vendorProfile.category?.replace("_", " ")}</p>
                    </div>
                    <div>
                      <Label className="text-white/60">Description</Label>
                      <p className="text-white/80 mt-1">{vendorProfile.description}</p>
                    </div>
                    <div>
                      <Label className="text-white/60">Services</Label>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {vendorProfile.services?.map((service, i) => (
                          <Badge key={i} className="bg-gold/10 text-gold border-gold/20" variant="outline">
                            {service}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Portfolio Section */}
                <div className="bg-charcoal rounded-xl border border-white/5 p-6 mt-6">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="font-heading text-lg font-bold text-white">Portfolio Images</h3>
                    <PortfolioUploader onUploadSuccess={fetchVendorProfile} />
                  </div>
                  
                  {vendorProfile.portfolio_images?.length > 0 ? (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {vendorProfile.portfolio_images.map((image, i) => (
                        <div key={i} className="relative group aspect-square rounded-xl overflow-hidden">
                          <img 
                            src={image.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${image}` : image}
                            alt={`Portfolio ${i + 1}`}
                            className="w-full h-full object-cover"
                          />
                          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => handleDeletePortfolioImage(image)}
                              className="rounded-full"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12 border-2 border-dashed border-white/10 rounded-xl">
                      <Image className="w-12 h-12 text-gold/30 mx-auto mb-4" />
                      <p className="text-white/60 mb-2">No portfolio images yet</p>
                      <p className="text-white/40 text-sm">Upload images to showcase your work</p>
                    </div>
                  )}
                </div>
              </TabsContent>
            </Tabs>
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

// Portfolio Uploader Component
const PortfolioUploader = ({ onUploadSuccess }) => {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Invalid file type. Allowed: JPEG, PNG, WebP, GIF");
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File too large. Maximum size: 5MB");
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      await axios.post(`${API}/upload/portfolio`, formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success("Image uploaded successfully!");
      onUploadSuccess?.();
    } catch (error) {
      console.error("Upload error:", error);
      toast.error(error.response?.data?.detail || "Failed to upload image");
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        onChange={handleFileSelect}
        className="hidden"
      />
      <Button
        onClick={() => fileInputRef.current?.click()}
        disabled={uploading}
        className="bg-gold text-black hover:bg-gold-light rounded-full"
        data-testid="upload-portfolio-btn"
      >
        {uploading ? (
          <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin mr-2"></div>
        ) : (
          <Upload className="w-4 h-4 mr-2" />
        )}
        Upload Image
      </Button>
    </>
  );
};

export default VendorDashboard;
